// "Carica altre uscite" + ricerca live + filtro per tipo per la griglia
// dell'archivio Rassegna Stampa (archive-rassegna_stampa.php), sostenute
// dall'endpoint REST castro/v1/rassegna-stampa
// (inc/rassegna-stampa-rest-controller.php). Stesso principio di
// assets/js/archivio-collaborazioni.js (che vedi per i commenti estesi sul
// perché il markup PHP renderizza server-side solo la prima pagina, con la
// paginazione standard di WordPress come fallback senza JS): qui i chip di
// filtro filtrano per tipo (tassonomia rassegna_tag) invece che per brand, e
// si combinano con la ricerca testuale (non la sostituiscono).
import { animateCardsIn, animateCardsOut } from "./archivio-card-animations";

const SEARCH_DEBOUNCE_MS = 300;

function debounce(fn, delay) {
    let timer = null;

    return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
    };
}

function initArchivioRassegnaStampaSection(section) {
    const grid = section.querySelector("[data-rassegna-grid]");

    if (!grid) {
        return;
    }

    const searchInput = section.querySelector("[data-rassegna-search-input]");
    const emptyMessage = section.querySelector("[data-rassegna-search-empty]");
    const countLabel = section.querySelector("[data-rassegna-count]");
    const loadMoreWrap = section.querySelector("[data-rassegna-load-more-wrap]");
    const loadMoreButton = section.querySelector("[data-rassegna-load-more]");
    const showLessButton = section.querySelector("[data-rassegna-show-less]");
    const shownCountLabel = section.querySelector("[data-rassegna-shown-count]");
    const loadMoreError = section.querySelector("[data-rassegna-load-more-error]");
    const pagination = section.querySelector(".archivio-rassegna-stampa__pagination");
    const filterButtons = Array.from(section.querySelectorAll("[data-rassegna-filter]"));
    const viewButtons = Array.from(section.querySelectorAll("[data-rassegna-view]"));

    const settings = (window.Castro && window.Castro.settings) || {};
    const apiUrl = `${settings.api_url || ""}castro/v1/rassegna-stampa`;

    // Quanto è già renderizzato server-side (vedi $rassegna_shown in
    // archive-rassegna_stampa.php): è anche la dimensione a cui torna "Mostra meno".
    const baseCount = parseInt(loadMoreWrap ? loadMoreWrap.dataset.rassegnaShown : "0", 10) || 0;

    let shown = baseCount;
    let total = parseInt(section.dataset.rassegnaTotal, 10) || 0;
    let currentSearch = "";
    let currentTag = "";
    // Scarta le risposte di richieste superate (l'utente ha digitato/cliccato
    // altro nel frattempo): solo l'ultima richiesta partita può aggiornare il DOM.
    let requestId = 0;

    // Con JS attivo la paginazione classica (unico modo di sfogliare l'elenco
    // senza JS) lascia il posto a "Carica altre uscite"/ricerca/filtro.
    if (pagination) {
        pagination.hidden = true;
    }

    function updateCount(value) {
        if (!countLabel) {
            return;
        }

        const label = value === 1 ? "uscita" : "uscite";
        countLabel.textContent = `${value} ${label}`;
    }

    function updateLoadMoreUi(hasMore) {
        if (loadMoreWrap) {
            loadMoreWrap.hidden = total <= baseCount;
        }

        if (loadMoreButton) {
            loadMoreButton.hidden = !hasMore;
        }

        if (showLessButton) {
            showLessButton.hidden = shown <= baseCount;
        }

        if (shownCountLabel) {
            shownCountLabel.textContent = total ? `Mostrate ${shown} di ${total} uscite` : "";
        }
    }

    async function fetchRassegnaStampa({ search, tag, offset, perPage }) {
        const params = new URLSearchParams({
            search,
            tag,
            offset: String(offset),
            per_page: String(perPage),
        });

        const response = await fetch(`${apiUrl}?${params.toString()}`, {
            headers: { Accept: "application/json" },
        });

        if (!response.ok) {
            throw new Error(`Richiesta fallita (${response.status})`);
        }

        return response.json();
    }

    function runQuery() {
        const thisRequestId = ++requestId;
        const perPage = baseCount;

        fetchRassegnaStampa({ search: currentSearch, tag: currentTag, offset: 0, perPage })
            .then((data) => {
                if (thisRequestId !== requestId) {
                    return;
                }

                grid.innerHTML = data.html;
                shown = data.shown;
                total = data.total;

                if (emptyMessage) {
                    emptyMessage.hidden = total > 0;
                }

                updateCount(total);
                updateLoadMoreUi(data.hasMore);
                animateCardsIn(Array.from(grid.children));
            })
            .catch((error) => {
                // Ricerca/filtro non critici: se la richiesta fallisce si
                // lascia la griglia com'era, senza rompere il resto della pagina.
                console.error("[archivio-rassegna-stampa]", error);
            });
    }

    const debouncedSearch = debounce(runQuery, SEARCH_DEBOUNCE_MS);

    if (searchInput) {
        searchInput.addEventListener("input", () => {
            currentSearch = searchInput.value.trim();
            debouncedSearch();
        });
    }

    filterButtons.forEach((button) => {
        button.addEventListener("click", () => {
            if (button.classList.contains("is-active")) {
                return;
            }

            currentTag = button.dataset.rassegnaFilterValue || "";
            filterButtons.forEach((btn) => btn.classList.toggle("is-active", btn === button));
            runQuery();
        });
    });

    if (loadMoreButton) {
        loadMoreButton.addEventListener("click", () => {
            const thisRequestId = ++requestId;

            if (loadMoreError) {
                loadMoreError.hidden = true;
            }
            loadMoreButton.classList.add("is-loading");

            fetchRassegnaStampa({ search: currentSearch, tag: currentTag, offset: shown, perPage: baseCount })
                .then((data) => {
                    if (thisRequestId !== requestId) {
                        return;
                    }

                    const previousCardCount = grid.children.length;

                    // data.shown è già "offset + card restituite in questa
                    // risposta" (calcolato server-side): sostituisce shown
                    // per intero, non si somma a quello precedente.
                    grid.insertAdjacentHTML("beforeend", data.html);
                    shown = data.shown;
                    total = data.total;
                    updateLoadMoreUi(data.hasMore);
                    animateCardsIn(Array.from(grid.children).slice(previousCardCount));
                })
                .catch((error) => {
                    console.error("[archivio-rassegna-stampa]", error);
                    if (loadMoreError) {
                        loadMoreError.hidden = false;
                    }
                })
                .finally(() => {
                    if (thisRequestId === requestId) {
                        loadMoreButton.classList.remove("is-loading");
                    }
                });
        });
    }

    // Solo una classe sul contenitore, nessun effetto su quali card sono
    // visibili o su come vengono caricate (stesso principio di
    // assets/js/podcast-video.js): il markup restituito dalla ricerca/"Carica
    // altre uscite" via REST non porta la classe, per questo il toggle vive
    // sul contenitore invece che sulle singole card.
    viewButtons.forEach((button) => {
        button.addEventListener("click", () => {
            const view = button.dataset.rassegnaView;

            if (button.classList.contains("is-active")) {
                return;
            }

            viewButtons.forEach((btn) => btn.classList.toggle("is-active", btn === button));
            grid.classList.toggle("archivio-rassegna-stampa__grid--list", view === "list");
        });
    });

    if (showLessButton) {
        showLessButton.addEventListener("click", () => {
            const cards = Array.from(grid.querySelectorAll(".archivio-rassegna-stampa__card")).slice(baseCount);

            showLessButton.classList.add("is-loading");
            animateCardsOut(cards, () => {
                showLessButton.classList.remove("is-loading");
                shown = baseCount;
                updateLoadMoreUi(total > baseCount);
            });
        });
    }

    // Card raggruppate (stessa notizia su più testate, vedi
    // castro_group_rassegna_items() in inc/template-helpers.php): un bottone
    // al posto del solito <a>, che apre la modale generica dei link
    // (template-parts/partials/links-modal.php) invece di navigare subito
    // via dal sito - gestita da assets/js/links-modal.js con un listener
    // delegato su document (non serve reinizializzarlo qui: funziona anche
    // per i bottoni/modali che arrivano dopo via innerHTML da ricerca/filtro/
    // "Carica altre uscite").

    updateLoadMoreUi(total > shown);
}

export function initArchivioRassegnaStampa() {
    document.querySelectorAll("[data-archivio-rassegna-stampa]").forEach(initArchivioRassegnaStampaSection);
}
